'use client';

import './combobox-field.scss';
import type { ReactNode } from 'react';
import { type InputHTMLAttributes, type KeyboardEvent, useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import { cn } from '../../utils/helpers.ts';
import { TooltipInfo } from '../tooltip-info/tooltip-info.tsx';
export interface ComboboxItem<T = unknown> {
  /** Display text shown in the dropdown. */
  label: string;
  /** Value submitted when the item is selected. */
  value: string;
  /** Optional metadata passed through on select. */
  data?: T;
}
export interface ComboboxFieldProps {
  /** Accessible label for the input. */
  'aria-label'?: string;
  /** Additional class names for the root element. */
  className?: string;
  /** Controlled input value (displayed in the trigger). */
  value?: string;
  /** Called when the display value changes. */
  onChange?: (value: string) => void;
  /** Called when an option is selected from the dropdown. */
  onSelect?: (item: ComboboxItem) => void;
  /** Called when the user submits (Enter key) with the current search value. */
  onSubmit?: (value: string) => void;
  /** Suggestion items to display in the dropdown. */
  items?: ComboboxItem[];
  /** Placeholder text for the trigger. */
  placeholder?: string;
  /** Placeholder text for the search input inside the popup. */
  searchPlaceholder?: string;
  /** Visible label above the input. */
  label?: string;
  /** Input name attribute. */
  name?: string;
  /** HTML input type for search input. */
  type?: 'text' | 'tel' | 'number';
  /** Input mode for virtual keyboards. */
  inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  /** Maximum character length. */
  maxLength?: number;
  /** Pattern for input validation. */
  pattern?: string;
  /** Whether the dropdown is loading. */
  isLoading?: boolean;
  /** Text shown when items is empty and search has a value. */
  noResultsText?: string;
  /** Minimum characters before showing suggestions (input variant). */
  minChars?: number;
  /** Disable the field. */
  disabled?: boolean;
  /** Mark the field as required. */
  required?: boolean;
  /** Ref to the underlying trigger element. */
  controlRef?: React.RefObject<HTMLElement>;
  /** Help text displayed below the input. */
  help?: string | ReactNode;
  /** Error message to display. When set, the field shows an error state. */
  error?: string;
  /** Tooltip content shown as an info icon beside the input. */
  tooltip?: string | ReactNode;
  /**
   * Variant mode:
   * - 'input': Trigger shows value; popup has a search input to filter the list.
   * - 'select': Trigger shows value; clicking opens all items (no search).
   */
  variant?: 'input' | 'select';
  /** Optional prefix displayed before the selected value. */
  selectedPrefix?: string;
  /** Icon name to display inside the trigger (leading position). */
  iconName?: IconNames;
  /** Optional item rendered at the top of the list (e.g. "Use my location"). */
  headerAction?: {
    label: string;
    icon?: IconNames;
    onSelect: () => void;
  };
}
export const ComboboxField: React.FC<ComboboxFieldProps> = ({
  'aria-label': ariaLabel,
  className,
  value = '',
  onChange,
  onSelect,
  onSubmit,
  items = [],
  placeholder,
  searchPlaceholder,
  label,
  name,
  type = 'text',
  inputMode,
  maxLength,
  pattern,
  isLoading,
  noResultsText,
  minChars = 1,
  disabled = false,
  required = false,
  controlRef,
  help,
  error,
  tooltip,
  variant = 'input',
  selectedPrefix,
  iconName,
  headerAction
}) => {
  const {
    t
  } = useTranslation(['core']);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [searchValue, setSearchValue] = useState('');
  const listboxId = useId();
  const triggerRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isSelectMode = variant === 'select';
  const isInputMode = variant === 'input';
  const shouldShowDropdown = isOpen && (items.length > 0 || noResultsText || isLoading);

  // Focus search input when popup opens in input mode
  useEffect(() => {
    if (isOpen && isInputMode && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, isInputMode]);
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchValue(newValue);
    onChange?.(newValue);
    setActiveIndex(-1);
  };
  const handleSelect = useCallback((item: ComboboxItem) => {
    const displayValue = isSelectMode ? `${selectedPrefix || ''}${item.label}` : item.value;
    onChange?.(displayValue);
    onSelect?.(item);
    setIsOpen(false);
    setActiveIndex(-1);
    setSearchValue('');
    // Return focus to trigger
    triggerRef.current?.focus();
  }, [onChange, onSelect, isSelectMode, selectedPrefix]);
  const handleKeyDown = (e_0: KeyboardEvent<HTMLInputElement>) => {
    const hasHeader = !!headerAction;
    const totalOptions = items.length + (hasHeader ? 1 : 0);
    switch (e_0.key) {
      case 'ArrowDown':
        {
          e_0.preventDefault();
          if (isOpen) {
            setActiveIndex(prev_0 => prev_0 < totalOptions - 1 ? prev_0 + 1 : prev_0);
          } else {
            setIsOpen(true);
            setActiveIndex(0);
          }
          break;
        }
      case 'ArrowUp':
        {
          e_0.preventDefault();
          setActiveIndex(prev => prev > 0 ? prev - 1 : -1);
          break;
        }
      case 'Home':
        {
          if (isOpen && totalOptions > 0) {
            e_0.preventDefault();
            setActiveIndex(0);
          }
          break;
        }
      case 'End':
        {
          if (isOpen && totalOptions > 0) {
            e_0.preventDefault();
            setActiveIndex(totalOptions - 1);
          }
          break;
        }
      case 'Enter':
        {
          e_0.preventDefault();
          if (activeIndex >= 0) {
            if (hasHeader && activeIndex === 0) {
              headerAction.onSelect();
              setIsOpen(false);
              setActiveIndex(-1);
              setSearchValue('');
              triggerRef.current?.focus();
            } else {
              const itemIndex_0 = hasHeader ? activeIndex - 1 : activeIndex;
              if (items[itemIndex_0]) {
                handleSelect(items[itemIndex_0]);
              }
            }
          } else if (isInputMode) {
            onSubmit?.(searchValue);
            setIsOpen(false);
            setSearchValue('');
            triggerRef.current?.focus();
          }
          break;
        }
      case 'Escape':
        {
          if (isOpen) {
            e_0.preventDefault();
            e_0.stopPropagation();
            setIsOpen(false);
            setActiveIndex(-1);
            setSearchValue('');
            triggerRef.current?.focus();
          }
          break;
        }
      case 'Tab':
        {
          if (isOpen) {
            if (activeIndex >= 0) {
              if (hasHeader && activeIndex === 0) {
                headerAction.onSelect();
              } else {
                const itemIndex = hasHeader ? activeIndex - 1 : activeIndex;
                if (items[itemIndex]) {
                  handleSelect(items[itemIndex]);
                }
              }
            }
            setIsOpen(false);
            setActiveIndex(-1);
            setSearchValue('');
          }
          break;
        }
      default:
        {
          break;
        }
    }
  };

  // Handle trigger click/keyboard to open
  const handleTriggerKeyDown = (e_1: KeyboardEvent<HTMLInputElement>) => {
    if (e_1.key === 'ArrowDown' || e_1.key === 'Enter' || e_1.key === ' ') {
      e_1.preventDefault();
      setIsOpen(true);
      setActiveIndex(isInputMode ? -1 : 0);
    }
  };

  // Scroll active option into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const childIndex = isInputMode ? activeIndex : activeIndex;
      const activeEl = listRef.current.children[childIndex] as HTMLElement;
      activeEl?.scrollIntoView({
        block: 'nearest'
      });
    }
  }, [activeIndex, isInputMode]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e_2: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e_2.target as Node)) {
        setIsOpen(false);
        setSearchValue('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const activeDescendantId = activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined;
  return <div ref={containerRef} className={cn('ComboboxField form-field', className, {
    'has-error': !!error,
    'ComboboxField--open': isOpen
  })}>
      {label && <label className="form-label label" htmlFor={`${listboxId}-input`}>
          {label}
        </label>}
      <div className="relative flex items-center">
        {iconName && <span className="ComboboxField__icon">
            <ArkIcon iconName={iconName} size="20" />
          </span>}
        <input ref={node => {
        (triggerRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
        if (controlRef) {
          (controlRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
        }
      }} id={`${listboxId}-input`} className="form-control" type="text" name={name} value={value} placeholder={placeholder} disabled={disabled} required={required} readOnly aria-label={ariaLabel} aria-invalid={!!error} aria-errormessage={error ? `${listboxId}-error` : undefined} aria-expanded={!!shouldShowDropdown} aria-haspopup="listbox" aria-controls={listboxId} role="combobox" autoComplete="off" onClick={() => {
        if (!disabled) {
          setIsOpen(!isOpen);
        }
      }} onKeyDown={handleTriggerKeyDown} />
        {tooltip && <TooltipInfo triggerClassName="Field__tooltip-icon" contentClassName="Field__tooltip" size="sm" alignOffset={-12} onPointerDownOutside={e_3 => {
        e_3.preventDefault();
      }}>
            {tooltip}
          </TooltipInfo>}
        {error && <span className="Field__error-icon">
            <ArkIcon iconName="alert-circle" size="20" />
          </span>}
        <span className={cn('ComboboxField__arrow', {
        'ComboboxField__arrow--open': !!shouldShowDropdown
      })} aria-hidden="true">
          <ArkIcon iconName="chevron-down" size="20" />
        </span>
      </div>
      {isOpen && <div id={listboxId} className="ComboboxField__listbox p-comfy-sm grid gap-snug-xl body-sm">
          {isInputMode && <div className="ComboboxField__search">
              <input ref={searchInputRef} className="form-control ComboboxField__search-input" type={type} inputMode={inputMode} maxLength={maxLength} pattern={pattern} value={searchValue} placeholder={searchPlaceholder || placeholder} aria-label={ariaLabel} aria-autocomplete="list" aria-activedescendant={activeDescendantId} autoComplete="off" onChange={handleSearchChange} onKeyDown={handleKeyDown} />
              <span className="ComboboxField__search-icon pr-snug-sm" aria-hidden="true">
                <ArkIcon iconName="search-sm" size="16" />
              </span>
            </div>}
          <ul ref={listRef} role="listbox" aria-label={label || ariaLabel || t('core:combobox.suggestionsLabel')} className="ComboboxField__options">
            {headerAction && <li id={`${listboxId}-option-0`} className={cn('ComboboxField__option flex gap-snug-xl items-center py-snug-xl', {
          'ComboboxField__option--active': !!headerAction && activeIndex === 0
        })} role="option" aria-selected={!!headerAction && activeIndex === 0} onMouseDown={() => {
          headerAction?.onSelect();
          setIsOpen(false);
        }}>
                {headerAction?.icon && <ArkIcon iconName={headerAction.icon} size="16" />}
                {headerAction?.label}
              </li>}
            {items.length > 0 && items.map((item_0, index) => {
          const optionIndex = headerAction ? index + 1 : index;
          return <li key={item_0.value} id={`${listboxId}-option-${optionIndex}`} role="option" aria-selected={optionIndex === activeIndex} className={cn('ComboboxField__option py-snug-xl', {
            'ComboboxField__option--active': optionIndex === activeIndex,
            'mt-snug-xl': headerAction
          })} onMouseDown={() => handleSelect(item_0)}>
                    {item_0.label}
                  </li>;
        })}
            {items.length === 0 && isLoading && <li className="ComboboxField__no-results pt-snug-xl" aria-live="polite">
                {noResultsText || 'Loading...'}
              </li>}
            {items.length === 0 && !isLoading && noResultsText && <li className="ComboboxField__no-results pt-snug-xl" aria-live="polite">
                {noResultsText}
              </li>}
          </ul>
        </div>}
      {help && <span className="ComboboxField__help form-label--below label label-mild">
          {help}
        </span>}
      {error && <div id={`${listboxId}-error`} className="Field__error mt-snug-lg" role="alert">
          {error}
        </div>}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {shouldShowDropdown && items.length > 0 ? t('core:combobox.suggestionsAvailable', {
        count: items.length
      }) : ''}
      </div>
    </div>;
};